import { COUNTRY_CODE_US, type CountryCode } from '@klear/constants/countries'
import isObject from 'lodash/isObject'
import { patchIsEmpty } from '@klear/schema/patch'

import type { SupplierCreateWire, SupplierUpdateWire } from '@klear/supplier/supplier.wire'
import type { Supplier } from '@klear/supplier'

type SupplierAddressForm = {
  country: CountryCode
  line1: string
  line2: string
  city: string
  state: string
  postalCode: string
}

export type SupplierForm = Omit<
  SupplierCreateWire,
  'address' | 'description' | 'website' | 'dunsNumber' | 'lei' | 'ein' | 'vatNumber' | 'parentOrganization'
> & {
  address: SupplierAddressForm
  description: string
  website: string
  dunsNumber: string
  lei: string
  ein: string
  vatNumber: string
  parentOrganization: string
  traderId?: number
  traderName?: string
}

export type SupplierFormDirty = Partial<Record<Exclude<keyof SupplierForm, 'address'>, boolean | object>> & {
  address?: boolean | Partial<Record<keyof SupplierAddressForm, boolean>>
}

export const EMPTY_SUPPLIER_FORM: SupplierForm = {
  address: {
    country: COUNTRY_CODE_US,
    line1: '',
    line2: '',
    city: '',
    state: '',
    postalCode: ''
  },
  name: '',
  description: '',
  website: '',
  dunsNumber: '',
  lei: '',
  ein: '',
  vatNumber: '',
  parentOrganization: ''
}

function optionalText(value: string | null | undefined): string | undefined {
  const trimmed = value?.trim()
  return trimmed ? trimmed : undefined
}

function clearableText(value: string | null | undefined): string | null {
  return optionalText(value) ?? null
}

function isDirty(dirty: SupplierFormDirty, key: Exclude<keyof SupplierForm, 'address'>): boolean {
  return dirty[key] === true || (dirty[key] != null && isObject(dirty[key]))
}

function isAddressDirty(dirty: SupplierFormDirty, key: keyof SupplierAddressForm): boolean {
  return dirty.address === true || (isObject(dirty.address) && dirty.address[key] === true)
}

export function supplierReadToForm(source: Supplier): SupplierForm {
  const address = source.address
  return {
    ...EMPTY_SUPPLIER_FORM,
    address: {
      country: address.country ?? COUNTRY_CODE_US,
      line1: address.line1 ?? '',
      line2: address.line2 ?? '',
      city: address.city ?? '',
      state: address.state ?? '',
      postalCode: address.postalCode ?? ''
    },
    name: source.name,
    description: source.description ?? '',
    website: source.website ?? '',
    dunsNumber: source.dunsNumber ?? '',
    lei: source.lei ?? '',
    ein: source.ein ?? '',
    vatNumber: source.vatNumber ?? '',
    parentOrganization: source.parentOrganization ?? '',
    size: source.size,
    traderId: source.traderId
  }
}

export function supplierFormToCreateWire(form: SupplierForm): SupplierCreateWire {
  const wire: SupplierCreateWire = {
    address: {
      country: form.address.country
    },
    name: form.name.trim()
  }

  const line1 = optionalText(form.address.line1)
  if (line1 !== undefined) wire.address.line1 = line1
  const line2 = optionalText(form.address.line2)
  if (line2 !== undefined) wire.address.line2 = line2
  const city = optionalText(form.address.city)
  if (city !== undefined) wire.address.city = city
  const state = optionalText(form.address.state)
  if (state !== undefined) wire.address.state = state
  const postalCode = optionalText(form.address.postalCode)
  if (postalCode !== undefined) wire.address.postalCode = postalCode

  const description = optionalText(form.description)
  if (description !== undefined) wire.description = description
  if (form.size != null) wire.size = form.size
  const website = optionalText(form.website)
  if (website !== undefined) wire.website = website
  const dunsNumber = optionalText(form.dunsNumber)
  if (dunsNumber !== undefined) wire.dunsNumber = dunsNumber
  const lei = optionalText(form.lei)
  if (lei !== undefined) wire.lei = lei
  const ein = optionalText(form.ein)
  if (ein !== undefined) wire.ein = ein
  const vatNumber = optionalText(form.vatNumber)
  if (vatNumber !== undefined) wire.vatNumber = vatNumber
  const parentOrganization = optionalText(form.parentOrganization)
  if (parentOrganization !== undefined) wire.parentOrganization = parentOrganization

  return wire
}

export function supplierFormToUpdateWire(form: SupplierForm, dirty: SupplierFormDirty): SupplierUpdateWire {
  const wire: SupplierUpdateWire = {}
  const address: NonNullable<SupplierUpdateWire['address']> = {}

  if (isAddressDirty(dirty, 'country')) address.country = form.address.country
  if (isAddressDirty(dirty, 'line1')) address.line1 = clearableText(form.address.line1)
  if (isAddressDirty(dirty, 'line2')) address.line2 = clearableText(form.address.line2)
  if (isAddressDirty(dirty, 'city')) address.city = clearableText(form.address.city)
  if (isAddressDirty(dirty, 'state')) address.state = clearableText(form.address.state)
  if (isAddressDirty(dirty, 'postalCode')) address.postalCode = clearableText(form.address.postalCode)
  if (!patchIsEmpty(address)) wire.address = address

  if (isDirty(dirty, 'name')) wire.name = form.name.trim()
  if (isDirty(dirty, 'description')) wire.description = clearableText(form.description)
  if (isDirty(dirty, 'size')) wire.size = form.size ?? null
  if (isDirty(dirty, 'website')) wire.website = clearableText(form.website)
  if (isDirty(dirty, 'dunsNumber')) wire.dunsNumber = clearableText(form.dunsNumber)
  if (isDirty(dirty, 'lei')) wire.lei = clearableText(form.lei)
  if (isDirty(dirty, 'ein')) wire.ein = clearableText(form.ein)
  if (isDirty(dirty, 'vatNumber')) wire.vatNumber = clearableText(form.vatNumber)
  if (isDirty(dirty, 'parentOrganization')) wire.parentOrganization = clearableText(form.parentOrganization)

  return wire
}
